import { useCallback } from "react"
import { useMutation } from "urql"

import type { DeliveryPackagingFormValues } from "../types"

import type {
  LocationDeliveryPackagingPreferenceInput,
  UpsertLocationDeliveryPackagingPreferenceMutation,
} from "@/graphql/codegen/graphql"
import { UpsertLocationDeliveryPackagingPreferenceDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface SaveDeliveryPackagingInput {
  locationId: string
  formValues: DeliveryPackagingFormValues
}

export interface UseSaveDeliveryPackagingReturn {
  saveDeliveryPackaging: (input: SaveDeliveryPackagingInput) => Promise<boolean>
  isSaving: boolean
}

export const useSaveDeliveryPackaging = (): UseSaveDeliveryPackagingReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ fetching }, executeMutation] = useMutation<
    UpsertLocationDeliveryPackagingPreferenceMutation,
    { input: LocationDeliveryPackagingPreferenceInput }
  >(UpsertLocationDeliveryPackagingPreferenceDocument)

  const saveDeliveryPackaging = useCallback(
    async ({ locationId, formValues }: SaveDeliveryPackagingInput): Promise<boolean> => {
      const input: LocationDeliveryPackagingPreferenceInput = {
        shipToLocationId: locationId,
        preferredDeliveryDateReference: formValues.preferredDeliveryDateReference ?? undefined,
        packagingInstructions: formValues.packagingInstructions || undefined,
        insureSmallParcel: formValues.insureSmallParcel,
      }

      const { data, error } = await executeMutation({ input })
      const output = data?.locationDeliveryPackagingPreference.upsert

      const hasError = error || output?.__typename === "LocationDeliveryPackagingPreferenceUpsertFailure"

      if (hasError) {
        const failureMessage =
          output?.__typename === "LocationDeliveryPackagingPreferenceUpsertFailure"
            ? output.error.message
            : error?.message

        notify({
          type: NotificationType.Error,
          silent: false,
          message: failureMessage ?? "Failed to save delivery and packaging preferences. Please try again.",
        })
        return false
      }

      notify({
        type: NotificationType.Success,
        silent: false,
        content: "Delivery & Packaging preferences saved successfully.",
      })
      return true
    },
    [executeMutation, notify]
  )

  return { saveDeliveryPackaging, isSaving: fetching }
}
